import { Injectable, signal, inject } from '@angular/core';
import { ChatApi } from './chat.api';
import { ChatMessage } from '../models/chat.models';
import { AuthService } from '../../auth/data-access/auth.service';

@Injectable({ providedIn: 'root' })
export class ChatService {
    private readonly api = inject(ChatApi);
    private readonly authService = inject(AuthService);

    readonly isOpen = signal(false);
    readonly isLoading = signal(false);
    readonly messages = signal<ChatMessage[]>([]);
    readonly sessionId = signal<string | null>(null);

    toggle() {
        this.isOpen.update((v) => !v);
    }

    open() {
        this.isOpen.set(true);
    }

    close() {
        this.isOpen.set(false);
    }

    async sendMessage(question: string): Promise<void> {
        if (!question.trim() || this.isLoading()) return;

        // Cookie-session auth: make sure a CSRF token is in memory before the
        // mutating streaming POST (the HttpClient interceptor can't see fetch()).
        try {
            await this.authService.ensureCsrfReady();
        } catch {
            return;
        }
        const csrfToken = this.authService.csrfToken();
        if (!csrfToken) return;

        // Add user message immediately
        this.messages.update((msgs) => [
            ...msgs,
            { role: 'user', content: question, timestamp: new Date() },
        ]);

        // Add empty assistant message placeholder for streaming
        this.messages.update((msgs) => [
            ...msgs,
            { role: 'assistant', content: '', timestamp: new Date(), isStreaming: true },
        ]);

        this.isLoading.set(true);

        try {
            const stream = this.api.streamUserMessage(question, this.sessionId(), csrfToken);

            for await (const chunk of stream) {
                if (chunk.error) {
                    this.updateLastMessage('Sorry, something went wrong. Please try again.', false);
                    break;
                }

                if (chunk.done) {
                    if (chunk.sessionId) this.sessionId.set(chunk.sessionId);
                    this.markLastMessageDone();
                    break;
                }

                if (chunk.text) {
                    this.appendToLastMessage(chunk.text);
                }
            }
        } catch {
            this.updateLastMessage('Unable to reach the chat service. Please try again.', false);
        } finally {
            this.isLoading.set(false);
        }
    }

    clearSession() {
        this.messages.set([]);
        this.sessionId.set(null);
    }

    private appendToLastMessage(text: string) {
        this.messages.update((msgs) => {
            const updated = [...msgs];
            const last = updated[updated.length - 1];
            if (last?.role === 'assistant') {
                updated[updated.length - 1] = { ...last, content: last.content + text };
            }
            return updated;
        });
    }

    private updateLastMessage(content: string, isStreaming: boolean) {
        this.messages.update((msgs) => {
            const updated = [...msgs];
            const last = updated[updated.length - 1];
            if (last?.role === 'assistant') {
                updated[updated.length - 1] = { ...last, content, isStreaming };
            }
            return updated;
        });
    }

    private markLastMessageDone() {
        this.messages.update((msgs) => {
            const updated = [...msgs];
            const last = updated[updated.length - 1];
            if (last?.role === 'assistant') {
                updated[updated.length - 1] = { ...last, isStreaming: false };
            }
            return updated;
        });
    }
}
